import { useEffect, useState } from "react";
import { Modal } from "antd";
import dayjs from "dayjs";
import { useAppDispatch, useAppSelector } from "../store/hooks";
import { loadPrivacy, requestAccountDeletion, saveAIConsent } from "../store/privacySlice";
import { clearAuthState } from "../store/authSlice";
import { authLogout, clearPersistedAccessToken, redirectToPublicApp } from "../auth/authClient";
import { Button } from "./ui/Button";

export const AccountPrivacyPanel = () => {
  const dispatch = useAppDispatch();
  const { info, loading, saving, deleting, error } = useAppSelector((state) => state.privacy);
  const [showDeletion, setShowDeletion] = useState(false);
  const [confirmation, setConfirmation] = useState("");
  const [acknowledged, setAcknowledged] = useState(false);
  const [accepted, setAccepted] = useState(false);
  const [scheduledFor, setScheduledFor] = useState<string | null>(null);

  useEffect(() => { dispatch(loadPrivacy()); }, [dispatch]);

  const handleWithdraw = () => {
    if (info) dispatch(saveAIConsent({ granted: false, version: info.aiDisclosure.version }));
  };
  const handleDelete = async () => {
    if (!acknowledged || confirmation !== "DELETE") return;
    const result = await dispatch(requestAccountDeletion(confirmation));
    if (!requestAccountDeletion.fulfilled.match(result)) return;
    setScheduledFor(result.payload.scheduledFor);
    setAccepted(true);
    clearPersistedAccessToken();
  };
  const handleFinish = async () => {
    dispatch(clearAuthState());
    try { await authLogout(); } finally { redirectToPublicApp(); }
  };

  return (
    <section className="space-y-4 rounded-xl border border-slate-200 bg-white p-6" aria-labelledby="account-privacy-heading" data-testid="account-privacy-panel">
      <h2 id="account-privacy-heading" className="text-lg font-semibold text-slate-900">Privacy & Account</h2>
      {loading && <p role="status">Loading privacy choices…</p>}
      {error && <p role="alert" className="text-sm text-red-700">{error}</p>}
      {info && <>
        <p className="text-sm text-slate-700">{info.aiConsent.granted ? "AI sharing with OpenAI is enabled." : "AI sharing is off. Manual planning is available."}</p>
        <p className="text-sm text-slate-600">Withdrawing permission stops future requests. It cannot recall information already sent or a request already in progress.</p>
        {info.aiConsent.granted && <Button data-testid="withdraw-ai-consent" variant="secondary" onClick={handleWithdraw} disabled={loading || saving || deleting}>Withdraw AI Permission</Button>}
        <div>
          <Button data-testid="open-account-deletion" variant="danger" onClick={() => setShowDeletion(true)} disabled={loading || saving || deleting || !info.deletion.enabled}>Delete Account</Button>
          {!info.deletion.enabled && <p className="mt-2 text-sm text-slate-600">Account deletion setup is pending. Contact support@dreamsup.com for help.</p>}
        </div>
      </>}
      <Button data-testid="refresh-privacy" variant="ghost" onClick={() => dispatch(loadPrivacy())} disabled={loading || saving || deleting}>Refresh Privacy Choices</Button>
      <nav className="flex flex-wrap gap-4 text-sm text-blue-700" aria-label="Legal information">
        <a href="https://www.dreamsup.com/privacy" target="_blank" rel="noopener noreferrer">Privacy Policy</a>
        <a href="https://www.dreamsup.com/terms" target="_blank" rel="noopener noreferrer">Terms</a>
        <a href="mailto:support@dreamsup.com">Email Support</a>
      </nav>
      <div className="space-y-2 border-t border-slate-200 pt-4 text-sm text-slate-600">
        <h3 className="font-semibold text-slate-900">Support — We're here to help.</h3>
        <p>Have a question, found a bug, or need a hand getting set up? Reach out and a real person will get back to you.</p>
        <p>Email: <a className="text-blue-700" href="mailto:support@dreamsup.com">support@dreamsup.com</a></p>
        <p>Support hours: Monday to Friday, 9:00 AM – 5:00 PM (Singapore Standard Time, GMT+8)</p>
      </div>
      <Modal title={accepted ? "Deletion requested" : "Permanently delete your account"} open={showDeletion} onCancel={() => setShowDeletion(false)} footer={null} closable={!deleting && !accepted} maskClosable={!deleting && !accepted} keyboard={!deleting && !accepted}>
        {accepted ? <div className="space-y-4" role="status">
          <p>Your request was accepted and access is blocked. Permanent cleanup is scheduled{scheduledFor ? ` for ${dayjs(scheduledFor).format("D MMMM YYYY")}` : " within the stated deletion window"}.</p>
          <p>Contact support@dreamsup.com if you need help. This request cannot be undone.</p>
          <Button data-testid="finish-account-deletion" onClick={handleFinish}>Finish and Sign Out</Button>
        </div> : <div className="space-y-4">
          <p>Access is blocked when your request is accepted. Dreams, goals, milestones, notes, attachments and profile information are scheduled for permanent deletion within {info?.deletion.delayDays ?? 30} days. This cannot be undone.</p>
          <p>Linked Stripe subscriptions are canceled during cleanup. Deleting DreamsUp does not cancel App Store subscriptions. Legally required financial records, limited security records and provider backups may be retained as described in the Privacy Policy.</p>
          <a className="text-blue-700 underline" href="https://apps.apple.com/account/subscriptions" target="_blank" rel="noopener noreferrer">Manage App Store Subscriptions</a>
          <label className="flex items-start gap-2"><input data-testid="deletion-acknowledgement" type="checkbox" checked={acknowledged} onChange={(event) => setAcknowledged(event.target.checked)} disabled={deleting} /><span>I understand the permanent deletion and subscription consequences.</span></label>
          <label className="block">Type DELETE to confirm<input data-testid="deletion-confirmation" className="mt-1 w-full rounded-md border border-slate-300 p-2" value={confirmation} onChange={(event) => setConfirmation(event.target.value)} disabled={deleting} autoComplete="off" /></label>
          {error && <p role="alert" className="text-red-700">{error}</p>}
          <div className="flex justify-end gap-2">
            <Button data-testid="cancel-account-deletion" variant="secondary" onClick={() => setShowDeletion(false)} disabled={deleting}>Cancel</Button>
            <Button data-testid="confirm-account-deletion" variant="danger" onClick={handleDelete} disabled={!acknowledged || confirmation !== "DELETE" || !info?.deletion.enabled} loading={deleting}>Request Permanent Deletion</Button>
          </div>
        </div>}
      </Modal>
    </section>
  );
};
